/**
 * Replays — the product, not a debug feature.
 *
 * The pitch's whole loop turns on the owner *seeing the reaction*: where the
 * raider hesitated, which bluff they respected, how far the spring threw them.
 * So a raid is recorded as what happened, not as inputs to be re-simulated:
 * position keyframes plus the event timeline. That means a replay is watchable
 * on any device, any build, forever — no physics determinism contract, no
 * version pinning, no "this replay is from an older patch".
 *
 * The cost is size, which is why frames are quantised and sampled at 20 Hz: a
 * 40-second raid is roughly 5 KB of JSON, small enough to keep several per room
 * in localStorage today and to POST to a Worker tomorrow.
 */

import type { RaidEvent, RaiderPose, RaiderState, SimSnapshot } from './types'

export const REPLAY_VERSION = 1 as const
export const REPLAY_HZ = 20
const FIELDS_PER_FRAME = 6

const POSES: RaiderPose[] = ['stand', 'run', 'air', 'flung', 'stuck']

export interface Replay {
  v: typeof REPLAY_VERSION
  roomId: string
  /** The room, inlined. A reaction must stay watchable even if the room changes. */
  roomCode: string
  raiderName: string
  outcome: 'escaped' | 'failed'
  durationMs: number
  hz: number
  /** Quantised keyframes, `FIELDS_PER_FRAME` numbers each. */
  frames: number[]
  events: RaidEvent[]
  createdAt: number
}

const q = (n: number) => Math.round(n * 100)
const dq = (n: number) => n / 100

export class ReplayRecorder {
  private frames: number[] = []
  private events: RaidEvent[] = []
  private nextSampleAt = 0
  private lastTime = 0

  constructor(
    private roomId: string,
    private roomCode: string,
    private raiderName: string,
  ) {}

  /** Call once per rendered frame with the current snapshot. Samples on its own clock. */
  observe(snap: SimSnapshot): void {
    this.lastTime = snap.time
    if (snap.time < this.nextSampleAt) return
    this.nextSampleAt = snap.time + 1 / REPLAY_HZ
    const r = snap.raider
    const poseIndex = Math.max(0, POSES.indexOf(r.pose))
    const flags = (r.carrying ? 1 : 0) | (r.grounded ? 2 : 0) | (poseIndex << 2)
    this.frames.push(q(r.x), q(r.y), q(r.z), q(r.yaw), q(r.tumble), flags)
  }

  add(events: readonly RaidEvent[]): void {
    for (const e of events) this.events.push(e)
  }

  finish(outcome: 'escaped' | 'failed', createdAt: number): Replay {
    return {
      v: REPLAY_VERSION,
      roomId: this.roomId,
      roomCode: this.roomCode,
      raiderName: this.raiderName,
      outcome,
      durationMs: Math.round(this.lastTime * 1000),
      hz: REPLAY_HZ,
      frames: this.frames,
      events: this.events,
      createdAt,
    }
  }
}

export function replayFrameCount(replay: Replay): number {
  return Math.floor(replay.frames.length / FIELDS_PER_FRAME)
}

/** Plays a recording back as interpolated `RaiderState`, like a live sim would. */
export class ReplayPlayer {
  readonly duration: number
  private count: number
  private cursor = 0

  constructor(readonly replay: Replay) {
    this.count = replayFrameCount(replay)
    this.duration = replay.durationMs / 1000
  }

  private frameAt(i: number): RaiderState {
    const f = this.replay.frames
    const o = Math.min(Math.max(i, 0), this.count - 1) * FIELDS_PER_FRAME
    const flags = f[o + 5] ?? 0
    return {
      x: dq(f[o] ?? 0),
      y: dq(f[o + 1] ?? 0),
      z: dq(f[o + 2] ?? 0),
      yaw: dq(f[o + 3] ?? 0),
      tumble: dq(f[o + 4] ?? 0),
      vx: 0,
      vy: 0,
      vz: 0,
      speed: 0,
      grounded: (flags & 2) !== 0,
      pose: POSES[(flags >> 2) & 0x7] ?? 'stand',
      carrying: (flags & 1) !== 0,
    }
  }

  /** Interpolated pose at time `t` seconds. */
  sample(t: number): RaiderState {
    if (this.count === 0) {
      return {
        x: 0,
        y: 0,
        z: 0,
        yaw: 0,
        tumble: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        speed: 0,
        grounded: true,
        pose: 'stand',
        carrying: false,
      }
    }
    const pos = t * this.replay.hz
    const i = Math.floor(pos)
    const frac = pos - i
    const a = this.frameAt(i)
    const b = this.frameAt(i + 1)
    const lerp = (x: number, y: number) => x + (y - x) * frac
    // Yaw wraps; take the short way round or the figure spins on its heel.
    let dyaw = b.yaw - a.yaw
    while (dyaw > Math.PI) dyaw -= Math.PI * 2
    while (dyaw < -Math.PI) dyaw += Math.PI * 2
    const vx = (b.x - a.x) * this.replay.hz
    const vz = (b.z - a.z) * this.replay.hz
    return {
      x: lerp(a.x, b.x),
      y: lerp(a.y, b.y),
      z: lerp(a.z, b.z),
      yaw: a.yaw + dyaw * frac,
      tumble: lerp(a.tumble, b.tumble),
      vx,
      vy: (b.y - a.y) * this.replay.hz,
      vz,
      speed: Math.hypot(vx, vz),
      grounded: a.grounded,
      pose: a.pose,
      carrying: a.carrying,
    }
  }

  /** Events newly crossed since the last call. Drives captions and markers. */
  drain(t: number): RaidEvent[] {
    const out: RaidEvent[] = []
    while (this.cursor < this.replay.events.length) {
      const e = this.replay.events[this.cursor]
      if (!e || e.t > t) break
      out.push(e)
      this.cursor++
    }
    return out
  }

  reset(): void {
    this.cursor = 0
  }
}

/**
 * The owner-facing reading of a reaction. This is what the pitch means by
 * "不是冷冰冰的通关率 37%" — it names the moment, not the metric.
 */
export interface ReactionDigest {
  outcome: 'escaped' | 'failed'
  seconds: number
  /** Where they stopped to think, in world space. */
  hesitations: Array<{ t: number; x: number; z: number }>
  /** Props that actually fired, most interesting first. */
  triggered: Array<{ iid: number; type: RaidEvent['type']; t: number }>
  /** Did they fall for a fake chest before finding the real one? */
  fellForDecoy: boolean
  /** Did they respect a harmless bluff plate? */
  respectedBluff: boolean
  launches: number
  pitfalls: number
  /** One line the owner reads first. */
  headline: string
}

export function digestReplay(replay: Replay): ReactionDigest {
  const hesitations: ReactionDigest['hesitations'] = []
  const triggered: ReactionDigest['triggered'] = []
  let fellForDecoy = false
  let respectedBluff = false
  let launches = 0
  let pitfalls = 0
  let pickupAt = Infinity

  for (const e of replay.events) {
    switch (e.type) {
      case 'hesitate':
        hesitations.push({ t: e.t, x: e.x, z: e.z })
        break
      case 'decoy':
        if (e.t < pickupAt) fellForDecoy = true
        break
      case 'bluffed':
        respectedBluff = true
        break
      case 'launched':
        launches++
        break
      case 'pitfall':
        pitfalls++
        break
      case 'pickup':
        pickupAt = Math.min(pickupAt, e.t)
        break
      default:
        break
    }
    if (e.iid !== undefined && e.type !== 'hesitate') {
      triggered.push({ iid: e.iid, type: e.type, t: e.t })
    }
  }

  const seconds = replay.durationMs / 1000
  return {
    outcome: replay.outcome,
    seconds,
    hesitations,
    triggered,
    fellForDecoy,
    respectedBluff,
    launches,
    pitfalls,
    headline: headlineFor(replay, {
      fellForDecoy,
      launches,
      pitfalls,
      reachedChest: pickupAt !== Infinity,
      hesitations,
    }),
  }
}

function headlineFor(
  replay: Replay,
  d: {
    fellForDecoy: boolean
    launches: number
    pitfalls: number
    reachedChest: boolean
    hesitations: ReactionDigest['hesitations']
  },
): string {
  const who = replay.raiderName || '一个陌生人'
  if (replay.outcome === 'escaped') {
    if (d.launches > 0) return `${who}被弹飞了 ${d.launches} 次，还是把宝箱带走了。`
    if (d.hesitations.length >= 3) return `${who}犹豫了 ${d.hesitations.length} 次，然后干净地通关了。`
    return `${who}一次都没中招。你的屋子被看穿了。`
  }
  // Order matters: the most humiliating true thing goes first, because that is
  // the line the owner will screenshot.
  if (d.pitfalls > 0) return `${who}掉进洞里 ${d.pitfalls} 次，最后空手离开。`
  if (d.reachedChest && d.launches > 0) return `${who}抱着宝箱被弹回来了，门在他面前锁上。`
  if (d.reachedChest) return `${who}拿到了宝箱，却没能在门锁上之前跑出去。`
  if (d.launches > 0) return `${who}还没摸到宝箱就被弹了 ${d.launches} 次。`
  if (d.fellForDecoy) return `${who}抱着假宝箱高兴了三秒钟。`
  return `${who}时间用完了，什么都没带走。`
}
